import { Bell, BellRing, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useUserAuth } from '../contexts/UserAuthContext';

/**
 * FollowButton — "Get Updates" toggle for athletes / dreamers.
 *
 * iter197 · Every tap surfaces a green success toast ("Now getting
 * updates" / "Stopped updates") so agents see clear confirmation
 * instead of the ambient red notification badge they were
 * confusing with an error.
 */
const FollowButton = ({ athleteId, athleteName, variant = 'pill', theme = 'dark', className = '' }) => {
  const { user, setLoginOpen, toggleFollow } = useUserAuth();
  const following = (user?.following || []).includes(athleteId);

  const toggle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!user) { setLoginOpen(true); return; }
    try {
      const res = await toggleFollow(athleteId);
      // res may be null when the auth modal was opened; only toast on real result.
      if (res) {
        const label = athleteName || 'this athlete';
        if (res.following) toast.success(`Now getting updates from ${label}.`, { duration: 2500 });
        else               toast.info(`Stopped updates from ${label}.`, { duration: 2500 });
      }
    } catch {
      // toggleFollow already surfaced its own error toast; no-op.
    }
  };

  if (variant === 'icon') {
    const Icon = following ? Check : Bell;
    return (
      <button type="button" onClick={toggle} aria-pressed={following}
        className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition active:scale-90 ${
          following ? 'bg-white text-black' : 'bg-black/60 text-white hover:bg-black/80 backdrop-blur'
        } ${className}`}
        data-testid={`follow-${athleteId}`}>
        <Icon className="w-4 h-4" />
      </button>
    );
  }

  const savedStyles   = theme === 'light' ? 'bg-black text-white' : 'bg-white text-black';
  const unsavedStyles = theme === 'light' ? 'bg-black/5 text-black hover:bg-black/10' : 'bg-white/10 text-white hover:bg-white/20';
  return (
    <button type="button" onClick={toggle} aria-pressed={following}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition active:scale-95 ${
        following ? savedStyles : unsavedStyles
      } ${className}`}
      data-testid={`follow-${athleteId}`}>
      {following ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
      {following ? 'Getting Updates' : 'Get Updates'}
    </button>
  );
};

export default FollowButton;
